import { createReducer, getType } from "typesafe-actions";

import type DateRange from "@mapmycustomers/shared/types/range/DateRange";

import DateRangeType from "@app/scene/reports/enums/DateRangeType";
import dateRangeTypeToDateRange from "@app/scene/reports/utils/dateRangeTypeToDateRange";
import Scope from "@app/types/dashboard/Scope";

import {
  EmailActivityActions,
  fetchBreakdown,
  fetchSummary,
  fetchTemplates,
  fetchTrend,
  requestEmailFeature,
  setBreakdownView,
  setDateRange,
  setDateRangeType,
  setScope,
} from "./actions";
import { BreakdownRow } from "./types/BreakdownRow";
import { EmailSummaryMetrics } from "./types/EmailSummaryMetrics";
import { TemplatePerformance } from "./types/TemplateRow";
import { TrendDataPoint } from "./types/TrendDataPoint";

export interface EmailActivityState {
  breakdown: { data: BreakdownRow[]; loading: boolean };
  /** Owner-only override of the breakdown grouping. Undefined = scope-derived (default). */
  breakdownView: "rep" | "team" | undefined;
  dateRange: DateRange;
  dateRangeType: DateRangeType;
  featureRequest: { loading: boolean; sent: boolean };
  scope: Scope;
  summaryCards: { data?: EmailSummaryMetrics; loading: boolean };
  templates: { data: TemplatePerformance[]; loading: boolean };
  trendChart: { data: TrendDataPoint[]; loading: boolean };
}

export const emailActivityInitialState: EmailActivityState = {
  breakdown: { data: [], loading: false },
  breakdownView: undefined,
  dateRange: dateRangeTypeToDateRange(DateRangeType.THIS_WEEK, true)!,
  dateRangeType: DateRangeType.THIS_WEEK,
  featureRequest: { loading: false, sent: false },
  scope: {},
  summaryCards: { loading: false },
  templates: { data: [], loading: false },
  trendChart: { data: [], loading: false },
};

const emailActivity = createReducer<EmailActivityState, EmailActivityActions>(
  emailActivityInitialState
)
  .handleType(getType(setDateRange), (state, { payload }) => ({
    ...state,
    dateRange: payload,
  }))
  .handleType(getType(setDateRangeType), (state, { payload }) => {
    // Non-custom types resolve to a concrete DateRange immediately; custom leaves the
    // existing range untouched (the UI collects the custom range via setDateRange).
    const resolvedRange = dateRangeTypeToDateRange(payload, true);
    return {
      ...state,
      dateRange: resolvedRange ?? state.dateRange,
      dateRangeType: payload,
    };
  })
  .handleType(getType(setScope), (state, { payload }) => ({
    ...state,
    // Changing scope drops any active owner-toggle override so the derived
    // (scope-driven) view resumes — e.g. picking a specific team should always
    // land in By Rep view, regardless of a prior toggle.
    breakdownView: undefined,
    scope: payload,
  }))
  .handleType(getType(setBreakdownView), (state, { payload }) => ({
    ...state,
    breakdownView: payload,
  }))
  .handleType(getType(fetchBreakdown.request), (state) => ({
    ...state,
    breakdown: { ...state.breakdown, loading: true },
  }))
  .handleType(getType(fetchBreakdown.success), (state, { payload }) => ({
    ...state,
    breakdown: { data: payload, loading: false },
  }))
  .handleType(getType(fetchBreakdown.failure), (state) => ({
    ...state,
    breakdown: { ...state.breakdown, loading: false },
  }))
  .handleType(getType(fetchSummary.request), (state) => ({
    ...state,
    summaryCards: { ...state.summaryCards, loading: true },
  }))
  .handleType(getType(fetchSummary.success), (state, { payload }) => ({
    ...state,
    summaryCards: { data: payload, loading: false },
  }))
  .handleType(getType(fetchSummary.failure), (state) => ({
    ...state,
    summaryCards: { ...state.summaryCards, loading: false },
  }))
  .handleType(getType(fetchTemplates.request), (state) => ({
    ...state,
    templates: { ...state.templates, loading: true },
  }))
  .handleType(getType(fetchTemplates.success), (state, { payload }) => ({
    ...state,
    templates: { data: payload, loading: false },
  }))
  .handleType(getType(fetchTemplates.failure), (state) => ({
    ...state,
    templates: { ...state.templates, loading: false },
  }))
  .handleType(getType(fetchTrend.request), (state) => ({
    ...state,
    trendChart: { ...state.trendChart, loading: true },
  }))
  .handleType(getType(fetchTrend.success), (state, { payload }) => ({
    ...state,
    trendChart: { data: payload, loading: false },
  }))
  .handleType(getType(fetchTrend.failure), (state) => ({
    ...state,
    trendChart: { ...state.trendChart, loading: false },
  }))
  .handleType(getType(requestEmailFeature.request), (state) => ({
    ...state,
    featureRequest: { ...state.featureRequest, loading: true },
  }))
  .handleType(getType(requestEmailFeature.success), (state) => ({
    ...state,
    featureRequest: { loading: false, sent: true },
  }))
  .handleType(getType(requestEmailFeature.failure), (state) => ({
    ...state,
    featureRequest: { ...state.featureRequest, loading: false },
  }));

export default emailActivity;
